import React, { useState } from "react";
import { GripVertical } from "lucide-react";
import { REWARD_TYPES } from "./utils";

// Column size constants for better maintainability
const DRAG_COL_SIZE_WITH_SKU = '4';
const DRAG_COL_SIZE_WITHOUT_SKU = '8';
const SKU_COL_SIZE_WITH_DRAG = '4';

const OptionEdit = ({
  option,
  index,
  options,
  setOptions,
  handleOptionNameChange,
  handleOptionValueChange,
  handleDeleteValue,
  handleToggleValueDisabled,
  handleDeleteOption,
  handleDone,
  setEditModes,
  canAddVariants,
  opts,
  setVariants
}) => {
  const [nameError, setNameError] = useState("");
  const [valuesError, setValuesError] = useState("");
  const [dragOverValueId, setDragOverValueId] = useState(null);

  const clearBackendError = (containerId) => {
    const errorContainer = document.getElementById(containerId);
    if (errorContainer) {
      const describedById = errorContainer.getAttribute("aria-describedby");
      if (describedById) {
        const backendErrorElement = document.getElementById(describedById);
        if (backendErrorElement) {
          backendErrorElement.remove();
        }
        errorContainer.removeAttribute("aria-describedby");
      }
      errorContainer.innerHTML = '';
    }
  };

  const validateName = () => {
    if (!option.title.trim()) {
      setNameError("Option name cannot be empty");
      return false;
    }
    setNameError(""); // Clear error if valid
    return true;
  };

  const validateValues = () => {
    const hasValidValues = option.values.some(v => v.name.trim() !== '');
    if (!hasValidValues) {
      setValuesError("Option must have at least one value");
      return false;
    }
    setValuesError(""); // Clear error if valid
    return true;
  };

  // Drag and drop handlers for dimension variants
  const handleValueDragStart = (e, value) => {
    e.dataTransfer.setData('valueId', value.id.toString());
    e.dataTransfer.setData('optionId', option.id.toString());
    e.currentTarget.classList.add('opacity-50');
  };

  const handleValueDragOver = (e) => {
    e.preventDefault();
    
    // Only show visual feedback for non-empty values that can be valid drop targets
    const targetElement = e.currentTarget;
    const valueId = targetElement.id.split('_').pop();
    const targetValue = option.values.find(v => v.id.toString() === valueId);
    
    if (targetValue && targetValue.name && targetValue.name.trim() !== '') {
      setDragOverValueId(targetValue.id);
    }
  };

  const handleValueDragLeave = (e) => {
    setDragOverValueId(null);
  };

  const handleValueDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-50');
    setDragOverValueId(null);
  };

  const handleValueDrop = (e, targetValue) => {
    e.preventDefault();
    setDragOverValueId(null);
    
    const draggedValueId = e.dataTransfer.getData('valueId');
    const draggedOptionId = e.dataTransfer.getData('optionId');
    
    // Only allow reordering within the same option
    if (draggedOptionId !== option.id.toString()) {
      return;
    }

    // Don't allow dropping on empty values - they should not change position
    if (!targetValue.name || targetValue.name.trim() === '') {
      return;
    }

    const draggedValue = option.values.find(val => val.id.toString() === draggedValueId);
    
    // Don't allow dragging empty values
    if (!draggedValue || !draggedValue.name || draggedValue.name.trim() === '') {
      return;
    }
    
    if (draggedValue && targetValue.id !== draggedValue.id) {
      // Get the current order of values by their position in the array
      const updatedValues = [...option.values];
      const draggedIndex = updatedValues.findIndex(val => val.id === draggedValue.id);
      const targetIndex = updatedValues.findIndex(val => val.id === targetValue.id);
      
      // Swap the positions of dragged and target values
      [updatedValues[draggedIndex], updatedValues[targetIndex]] = [updatedValues[targetIndex], updatedValues[draggedIndex]];
      
      // Update the option with the new values order
      setOptions(prevOptions =>
        prevOptions.map(opt =>
          opt.id === option.id
            ? { ...opt, values: updatedValues }
            : opt
        )
      );
    }
  };

  const handleDoneClick = () => {
    const isNameValid = validateName();
    const isValuesValid = validateValues();
    
    if (isNameValid && isValuesValid) {
      handleDone(option.id, setEditModes);
    }
  };

  const handleValueChange = (optionId, valueId, field, newValue) => {
    handleOptionValueChange(optionId, valueId, field, newValue, setOptions);
    
    // Clear frontend values error when user starts adding/editing a value
    if (field === "name" && newValue.trim() !== '') {
      setValuesError("");
    }

    // Clear backend values error when user starts adding/editing a value
    clearBackendError(`reward_dimension_${index}_values`);
  };

  const handleTitleInputChange = (e) => {
    const newTitle = e.target.value;
    handleOptionNameChange(option.id, newTitle, setOptions);
    if (newTitle.trim() !== '') {
      setNameError(""); // Clear frontend name error on input change
    }
    clearBackendError(`reward_dimension_${index}_name`); // Clear backend name error on input change
  };

  const renderOptionNameInput = () => (
    <div className="mb-2">
      { (opts.reward_type === REWARD_TYPES.VARIABLE_TYPE) && <div className="ms-1 error-container" id="reward_variants"></div> }
      <label className="form-label">Option name</label>
      <div className="error-container" id={`reward_dimension_${index}_name`}></div>
      {nameError && <div className="alert alert-danger d-inline-block frontend-error">{nameError}</div>}

      <input
        type="text"
        className="form-control"
        id={`option_name_${index}`}
        placeholder="Enter option name"
        value={option.title}
        onChange={handleTitleInputChange}
        disabled={canAddVariants}
      />
    </div>
  );

  const renderValueInput = (value, valueIndex) => {
    const isVariableType = opts.reward_type === REWARD_TYPES.VARIABLE_TYPE;
    const isCompanyFulfilled = opts.reward_type === REWARD_TYPES.COMPANY_FULFILLED;
    const isAxomoReward = opts.reward_type === REWARD_TYPES.AXOMO_REWARD;
    const showSKU = isCompanyFulfilled || isAxomoReward;
    
    // Filter out disabled values for delete button logic
    const visibleValues = option.values.filter(v => !v.disabled);
    
    // Check if all visible values are empty (both name and sku)
    const allVisibleValuesEmpty = visibleValues.every(v => 
      v.name.trim() === '' && (!v.sku || v.sku.trim() === '')
    );
    
    const showDelete = !canAddVariants && 
      visibleValues.length > 1 && 
      !(visibleValues.length === 1 && allVisibleValuesEmpty);

    const isDisabled = value.disabled || canAddVariants;

    // Count non-empty visible values for drag logic
    const nonEmptyVisibleValues = visibleValues.filter(v => v.name && v.name.trim() !== '');
    
    const isValueEmpty = !value.name || value.name.trim() === '';
    const canBeDropTarget = !isValueEmpty && nonEmptyVisibleValues.length > 1; // Only allow drops when multiple non-empty values exist

    const dragProps = !isValueEmpty && nonEmptyVisibleValues.length > 1 ? {
      draggable: true,
      onDragStart: (e) => handleValueDragStart(e, value),
      style: { cursor: 'move' }
    } : {};

    const dropProps = canBeDropTarget ? {
      onDragOver: handleValueDragOver,
      onDrop: (e) => handleValueDrop(e, value),
      onDragLeave: handleValueDragLeave,
      onDragEnd: handleValueDragEnd
    } : {};

    // Calculate column classes for name input - always account for drag handle space
    const nameInputColClass = showSKU ? `col-md-${DRAG_COL_SIZE_WITH_SKU}` : `col-md-${DRAG_COL_SIZE_WITHOUT_SKU}`;

    // Apply drag over styling based on state
    const isDragOver = dragOverValueId === value.id;
    const valueItemClass = `mb-2 value-item ${isDragOver ? 'border-primary' : ''}`;

    return (
      <div 
        key={value.id} 
        id={`option_values_attributes_${valueIndex}`} 
        className={valueItemClass}
        {...dragProps}
        {...dropProps}
      >
        <div className="row align-items-center">
          <div className="col-auto pe-0">
            <div className="d-flex align-items-center justify-content-center" style={{ width: '24px', height: '38px' }}>
              <GripVertical size={16} className="text-secondary" />
            </div>
          </div>
          
          <div className={`col ${nameInputColClass} mb-2 mb-md-0`}>
            {isVariableType ? (
              <input
                type="number"
                className={`form-control ${value.disabled ? 'text-muted' : ''}`}
                id={`option_values_attributes_${valueIndex}_label`}
                placeholder="Enter value"
                value={value.name}
                min={opts.min_value}
                max={opts.max_value}
                step="any"
                onChange={(e) => handleValueChange(option.id, value.id, "name", e.target.value)}
                disabled={isDisabled}
              />
            ) : (
              <input
                type="text"
                className={`form-control ${value.disabled ? 'text-muted' : ''}`}
                id={`option_values_attributes_${valueIndex}_label`}
                placeholder="Enter value"
                value={value.name}
                onChange={(e) => handleValueChange(option.id, value.id, "name", e.target.value)}
                disabled={isDisabled}
              />
            )}
          </div>          
          {/* SKU input - breaks to new line on small screens, offset to align with first input */}
          {showSKU && (
            <div className="w-100 d-md-none"></div>
          )}
          {showSKU && (
            <div className="col-auto pe-0 d-md-none">
              <div className="d-flex align-items-center justify-content-center" style={{ width: '24px', height: '38px' }}>
              </div>
            </div>
          )}
          {showSKU && (
            <div className={`col col-md-${SKU_COL_SIZE_WITH_DRAG} mb-2 mb-md-0`}>
              <input
                type="text"
                className={`form-control ${value.disabled ? 'text-muted' : ''}`}
                id={`option_values_attributes_${valueIndex}_sku`}
                placeholder="SKU (optional)"
                value={value.sku}
                onChange={(e) => handleValueChange(option.id, value.id, "sku", e.target.value)}
                disabled={isDisabled}
              />
            </div>
          )}          
          {/* Delete button - breaks to new line on small screens, offset to align with first input */}
          {showDelete && (
            <div className="w-100 d-md-none"></div>
          )}
          {showDelete && (
            <div className="col-auto pe-0 d-md-none">
              <div className="d-flex align-items-center justify-content-center" style={{ width: '24px', height: '38px' }}>
              </div>
            </div>
          )}
          {showDelete && (
            <div className="col col-md-2 text-start">
              {isVariableType ? (
                <button
                  type="button"
                  className={"btn btn-sm btn-outline-warning"}
                  onClick={() => handleToggleValueDisabled(option.id, value.id, setOptions, setVariants)}
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-light-dark danger btn-sm"
                  onClick={() => handleDeleteValue(option.id, value.id, setOptions)}
                >
                  <i className="font-awesome-16 fa-regular fa-trash-can" aria-hidden="true"></i>
                </button>
              )}
            </div>
          )}
        </div>
        {valueIndex < visibleValues.length - 1 && (
          <hr className="d-md-none my-3" />
        )}
      </div>
    );
  };

  const renderOptionValues = () => {
    // Filter out disabled values for display
    const visibleValues = option.values.filter(value => !value.disabled);
    
    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between mb-1">
          <label className="form-label">Option values</label>
          {opts.reward_type === REWARD_TYPES.VARIABLE_TYPE && (
            <span className="mb-1">
              {`Min: ${opts.currency_symbol}${opts.min_value}, Max: ${opts.currency_symbol}${opts.max_value}`}
            </span>
          )}
        </div>
        <div className="error-container" id={`reward_dimension_${index}_values`}></div>
        {valuesError && <div className="alert alert-danger d-inline-block mb-2 frontend-error">{valuesError}</div>}
        {visibleValues.map((value, valueIndex) => {
          // Get the original index for proper ID generation
          const originalIndex = option.values.findIndex(v => v.id === value.id);
          return renderValueInput(value, originalIndex);
        })}
      </div>
    );
  };

  const renderDoneButton = () => {
    if (opts.reward_type === REWARD_TYPES.VARIABLE_TYPE) return null;

    return (
      <button 
        type="button"
        className="btn btn-primary"
        onClick={handleDoneClick}
      >
        Done
      </button>
    );
  };

  const renderDeleteButton = () => {
    if (canAddVariants || options.length <= 1) return null;

    return (
      <button
        type="button"
        className="btn btn-light-dark danger"
        onClick={() => handleDeleteOption(option.id, setOptions, setEditModes)}
      >
        <i className="font-awesome-16 fa-regular fa-trash-can" aria-hidden="true"></i>
      </button>
    );
  };

  const renderActionButtons = () => (
    <div className="d-flex justify-content-between">
      {renderDoneButton()}
      {renderDeleteButton()}
    </div>
  );

  return (
    <div id={`option_container_${index}`} className="p-2 border rounded option-item">
      {renderOptionNameInput()}
      {renderOptionValues()}
      {renderActionButtons()}
    </div>
  );
};

export default OptionEdit;